import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { ChartDataset } from "chart.js";
import { isEqual } from "lodash";

export type RotatePoint = (
  lon: number,
  lat: number,
) => { x: number; y: number };

export type TrackMapBounds = {
  minLon: number;
  maxLon: number;
  minLat: number;
  maxLat: number;
};

type UseBaseTrackMapOptions = {
  showFlagColors?: boolean;
  showYellowSectors?: boolean;
  showTimingLoops?: boolean;
  showTurnNumbers?: boolean;
};

// Shared scaling/rotation/track-rendering logic for GPS-based track maps. Feature-specific
// components (driver dots, projected points, etc.) are layered on top by the caller.
export function useBaseTrackMap({
  showFlagColors = false,
  showYellowSectors = false,
  showTimingLoops = false,
  showTurnNumbers = false,
}: UseBaseTrackMapOptions = {}) {
  const trackMapRotation = useSelector(
    (state: RootState) =>
      state.imports.imports.strategy?.data.trackMapRotation ?? 0,
  );

  const trackMap = useSelector(
    (state: RootState) => state.trackMap?.trackMap,
    isEqual,
  );

  const sessionStatus = useSelector(
    (state: RootState) => state.aks.v2.timing?.session?.status,
    isEqual,
  );

  // Colour the track edges based on the session's current flag status
  const trackColor = useMemo(() => {
    if (!showFlagColors) return "#ffffff";
    const status = sessionStatus?.currentFlag;
    if (status === "SAFETY_CAR" || status === "FULL_YELLOW") return "#ffff00";
    if (status === "RED") return "#ff0000";
    return "#ffffff";
  }, [showFlagColors, sessionStatus]);

  const trackSectionFlags = useSelector(
    (state: RootState) => state.aks.v2.raceControl?.trackSectionFlags,
    isEqual,
  );

  const flags = useSelector(
    (state: RootState) => state.aks.v2.timing?.session?.flags,
    isEqual,
  );

  // Get sectors with active yellow flags - prefer trackSectionFlags, fallback to flags events
  const yellowSectors = useMemo(() => {
    if (!showYellowSectors) return new Set<string>();

    // Use trackSectionFlags from raceControl if available
    if (trackSectionFlags && Object.keys(trackSectionFlags).length > 0) {
      const activeYellows = new Set<string>();
      Object.values(trackSectionFlags).forEach((sectorFlag) => {
        if (
          (sectorFlag?.flag === "YELLOW" ||
            sectorFlag?.flag === "DOUBLE_YELLOW") &&
          sectorFlag.section !== undefined
        ) {
          activeYellows.add(String(sectorFlag.section));
        }
      });
      return activeYellows;
    }

    // Fallback parse flags events
    if (!flags) return new Set<string>();

    const activeYellows = new Set<string>();

    const sortedFlags = Object.values(flags)
      .filter((flag) => flag !== null)
      .sort((a, b) => (a.time || 0) - (b.time || 0));

    for (const flag of sortedFlags) {
      if (flag.type === "GREEN" && !flag.info) {
        activeYellows.clear();
      } else if (flag.type === "FLAG_INFO" && flag.info) {
        const yellowMatch = flag.info.match(/YELLOW on (\d+)/);
        const greenMatch = flag.info.match(/GREEN on (\d+)/);

        if (yellowMatch) {
          activeYellows.add(yellowMatch[1]);
        } else if (greenMatch) {
          activeYellows.delete(greenMatch[1]);
        }
      }
    }

    return activeYellows;
  }, [showYellowSectors, trackSectionFlags, flags]);

  // Track the container's actual pixel size so the chart's x/y scales can be matched to it
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    // Measure synchronously before paint so the first render already has the right aspect ratio
    const rect = element.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      setContainerSize({ width: rect.width, height: rect.height });
    }

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) {
        setContainerSize({ width, height });
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // Rotate lon/lat points clockwise by trackMapRotation degrees around the track's geometric center
  const rotatePoint: RotatePoint = useMemo(() => {
    if (!trackMap?.trackEdges?.inner?.lat?.length || !trackMapRotation) {
      return (lon: number, lat: number) => ({ x: lon, y: lat });
    }

    const allLats = [
      ...trackMap.trackEdges.inner.lat,
      ...(trackMap.trackEdges.outer?.lat || []),
    ];
    const allLons = [
      ...trackMap.trackEdges.inner.lon,
      ...(trackMap.trackEdges.outer?.lon || []),
    ];
    const centerLat = (Math.min(...allLats) + Math.max(...allLats)) / 2;
    const centerLon = (Math.min(...allLons) + Math.max(...allLons)) / 2;
    const cosLat = Math.cos((centerLat * Math.PI) / 180);

    // Clockwise rotation (screen space has y increasing upward, same as latitude)
    const theta = (trackMapRotation * Math.PI) / 180;
    const cosTheta = Math.cos(theta);
    const sinTheta = Math.sin(theta);

    return (lon: number, lat: number) => {
      const localX = (lon - centerLon) * cosLat;
      const localY = lat - centerLat;
      const rotatedX = localX * cosTheta + localY * sinTheta;
      const rotatedY = localY * cosTheta - localX * sinTheta;
      return {
        x: centerLon + rotatedX / cosLat,
        y: centerLat + rotatedY,
      };
    };
  }, [trackMap, trackMapRotation]);

  // Build the common track map datasets: edges, timing lines, turn/loop markings, sectors
  const trackMapDatasets = useMemo(() => {
    const datasets: ChartDataset<"scatter">[] = [];

    if (!trackMap) return datasets;

    // Inner track edge
    if (trackMap.trackEdges?.inner?.lat?.length > 0) {
      const innerData = trackMap.trackEdges.inner.lat.map(
        (lat: number, i: number) =>
          rotatePoint(trackMap.trackEdges.inner.lon[i], lat),
      );
      datasets.push({
        data: innerData,
        borderColor: trackColor,
        backgroundColor: "transparent",
        pointRadius: 0,
        showLine: true,
        borderWidth: 1,
        fill: false,
        order: 100,
      });
    }

    // Outer track edge
    if (trackMap.trackEdges?.outer?.lat?.length > 0) {
      const outerData = trackMap.trackEdges.outer.lat.map(
        (lat: number, i: number) =>
          rotatePoint(trackMap.trackEdges.outer.lon[i], lat),
      );
      datasets.push({
        data: outerData,
        borderColor: trackColor,
        backgroundColor: "transparent",
        pointRadius: 0,
        showLine: true,
        borderWidth: 1,
        fill: false,
        order: 100,
      });
    }

    // Timing lines
    trackMap.lines?.forEach(
      (line: { name: string; lat: number[]; lon: number[] }) => {
        const lineData = line.lat.map((lat: number, i: number) =>
          rotatePoint(line.lon[i], lat),
        );
        datasets.push({
          data: lineData,
          borderColor: "#ffffff",
          backgroundColor: "transparent",
          pointRadius: 0,
          showLine: true,
          borderWidth: 2,
          fill: false,
          order: 90,
        });
      },
    );

    // Turn markings
    if (showTurnNumbers) {
      trackMap.turnMarkings?.forEach(
        (turn: { name: string; lat: number; lon: number }) => {
          // Handle both "15" and "T15" name formats
          const turnLabel = turn.name.startsWith("T")
            ? turn.name
            : `T${turn.name}`;
          datasets.push({
            label: turnLabel,
            data: [rotatePoint(turn.lon, turn.lat)],
            pointRadius: 0,
            backgroundColor: "transparent",
            borderColor: "transparent",
            pointBorderWidth: 0,
            showLine: false,
            order: 80,
          });
        },
      );
    }

    // Loop markings
    if (showTimingLoops) {
      trackMap.loopMarkings?.forEach(
        (loop: { name: string; lat: number; lon: number }) => {
          datasets.push({
            label: loop.name,
            data: [rotatePoint(loop.lon, loop.lat)],
            pointRadius: 0,
            showLine: false,
            order: 80,
          });
        },
      );
    }

    // Sectors - show Sector00 always in white, others when flagged as yellow by the caller
    trackMap.sectors?.forEach(
      (sector: { name: string; lat: number[]; lon: number[] }) => {
        // Extract sector number from name and normalize (e.g., "Sector07" -> "7")
        const sectorNum = String(parseInt(sector.name.replace(/\D/g, ""), 10));
        const isSectorZero = sectorNum === "0";
        const isYellow = yellowSectors.has(sectorNum);

        if ((isSectorZero || isYellow) && sector.lat.length > 0) {
          // Close the shape by adding first point at the end
          const sectorData = [
            ...sector.lat.map((lat: number, i: number) =>
              rotatePoint(sector.lon[i], lat),
            ),
            rotatePoint(sector.lon[0], sector.lat[0]),
          ];

          datasets.push({
            label: sector.name,
            data: sectorData,
            borderColor: isSectorZero ? "#ffffff" : "rgba(255, 255, 0, 0.8)",
            backgroundColor: "transparent",
            pointRadius: 0,
            showLine: true,
            borderWidth: 2,
            fill: false,
            order: 95, // Above track edges but below drivers
          });
        }
      },
    );

    return datasets;
  }, [
    trackMap,
    trackColor,
    yellowSectors,
    rotatePoint,
    showTurnNumbers,
    showTimingLoops,
  ]);

  // Calculate bounds with cosine correction, fitted to the container's actual pixel
  // aspect ratio so a degree of lon/lat covers the same number of pixels on both axes
  const bounds: TrackMapBounds = useMemo(() => {
    const aspect =
      containerSize && containerSize.height > 0
        ? containerSize.width / containerSize.height
        : 1;

    // Prefer track map bounds if available
    if (trackMap?.trackEdges?.inner?.lat?.length > 0) {
      const rotatedInner = trackMap.trackEdges.inner.lat.map(
        (lat: number, i: number) =>
          rotatePoint(trackMap.trackEdges.inner.lon[i], lat),
      );
      const rotatedOuter = (trackMap.trackEdges.outer?.lat || []).map(
        (lat: number, i: number) =>
          rotatePoint(trackMap.trackEdges.outer.lon[i], lat),
      );
      const rotatedPoints = [...rotatedInner, ...rotatedOuter];
      const allLats = rotatedPoints.map((p) => p.y);
      const allLons = rotatedPoints.map((p) => p.x);

      const minLon = Math.min(...allLons);
      const maxLon = Math.max(...allLons);
      const minLat = Math.min(...allLats);
      const maxLat = Math.max(...allLats);

      const centerLat = (minLat + maxLat) / 2;
      const centerLon = (minLon + maxLon) / 2;

      const cosLat = Math.cos((centerLat * Math.PI) / 180);

      const lonRangeAdjusted = (maxLon - minLon) * cosLat;
      const latRange = maxLat - minLat;

      // Grow whichever axis has spare room so the track's bounding box is fully
      // contained within a box that matches the container's aspect ratio
      let finalLonRangeAdjusted: number;
      let finalLatRange: number;
      if (lonRangeAdjusted / aspect >= latRange) {
        finalLonRangeAdjusted = lonRangeAdjusted;
        finalLatRange = lonRangeAdjusted / aspect;
      } else {
        finalLatRange = latRange;
        finalLonRangeAdjusted = latRange * aspect;
      }

      const padding = Math.max(finalLonRangeAdjusted, finalLatRange) * 0.05;
      finalLonRangeAdjusted += 2 * padding;
      finalLatRange += 2 * padding;

      const finalLonRange = finalLonRangeAdjusted / cosLat;

      return {
        minLon: centerLon - finalLonRange / 2,
        maxLon: centerLon + finalLonRange / 2,
        minLat: centerLat - finalLatRange / 2,
        maxLat: centerLat + finalLatRange / 2,
      };
    }

    // Fallback to GPS data bounds
    return {
      minLon: 0,
      maxLon: Math.max(aspect, 1),
      minLat: 0,
      maxLat: Math.max(1 / aspect, 1),
    };
  }, [trackMap, containerSize, rotatePoint]);

  return {
    containerRef,
    containerSize,
    trackMap,
    trackMapRotation,
    rotatePoint,
    trackMapDatasets,
    bounds,
  };
}
